'use client';

import React, { forwardRef, useMemo, useState } from 'react';

import type { CustomProps } from '../Input';
import { Input } from '../Input';
import { Button } from '../../Button/Button';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator/PasswordStrengthIndicator';
import { getEvaluationLevelFromPassword } from './PasswordStrengthIndicator/validatePassword';

export interface InputPasswordProps extends Omit<CustomProps, 'value'> {
  /** Label for show password base (text) button */
  showPasswordButtonLabelShow?: string;
  /** Label for show password base (text) button */
  showPasswordButtonLabelHide?: string;
  /** Chars counter label */
  charsCounterLabel?: string;
  value?: string;
  checkPasswordStrength?: boolean;
}

export const InputPassword = forwardRef<HTMLInputElement, InputPasswordProps>(
  (
    {
      showPasswordButtonLabelShow,
      showPasswordButtonLabelHide,
      charsCounterLabel,
      value = '',
      error,
      label,
      helperText,
      checkPasswordStrength = false,
      minLength = 6,
      maxLength = 256,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const toggleShowPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setShowPassword((s) => !s);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      props.onChange?.(e);
    };

    const level = useMemo(
      () => getEvaluationLevelFromPassword(value, minLength, maxLength),
      [value, minLength, maxLength],
    );

    return (
      <div>
        <Input
          ref={ref}
          label={label}
          error={error}
          helperText={helperText}
          {...props}
          autoComplete="off"
          onBlur={handleBlur}
          onChange={handleChange}
          type={showPassword ? 'text' : 'password'}
          className="hide-default-browser-icon rounded-sm"
        >
          <>
            {charsCounterLabel && (
              <p
                className={`text-size-body-sm absolute top-[-18px] right-0 ${
                  error ? 'text-color-grey-dark' : 'text-color-success-dark'
                }`}
              >
                {`${value?.length || 0} ${charsCounterLabel}`}
              </p>
            )}
            {showPasswordButtonLabelHide && showPasswordButtonLabelShow && (
              <Button
                onClick={toggleShowPassword}
                aria-label={
                  showPassword ? showPasswordButtonLabelHide : showPasswordButtonLabelShow
                }
                className="text-grey-light bg-color-transparent enabled:hover:bg-color-transparent absolute top-0 right-0 h-full border-0 px-1"
              >
                {showPassword ? showPasswordButtonLabelHide : showPasswordButtonLabelShow}
              </Button>
            )}
          </>
        </Input>
        {checkPasswordStrength && <PasswordStrengthIndicator level={level} />}
      </div>
    );
  },
);
